import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { AdminTabs } from "@/components/admin-tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar, Loader2, MessageCircle, User, Users, Check, X, Plus, Wine, MapPin, Pencil, FileDown, Share2, Phone } from "lucide-react";
import { COMPANY } from "@/lib/company-info";
import { downloadReservationsPdf, shareReservationsPdf } from "@/lib/reservation-report-pdf";


export const Route = createFileRoute("/harikaradmin/reservations")({
  head: () => ({ meta: [{ title: "Rezervasyonlar — JUËN Admin" }] }),
  component: AdminReservationsPage,
});

type Status = "pending" | "confirmed" | "rejected" | "cancelled";

interface Row {
  id: string;
  user_id: string | null;
  event_id: string;
  party_size: number;
  preferred_date: string;
  notes: string | null;
  status: Status;
  admin_notes: string | null;
  created_at: string;
  manual_name: string | null;
  bottle_count: number | null;
  bottle_type: string | null;
  is_manual: boolean;
  area?: string | null;
  contact?: string | null;
  profile?: { first_name: string; last_name: string; email: string; phone: string | null } | null;

  event?: { name: string; starts_at: string } | null;
}

const STATUS_LABEL: Record<Status, string> = {
  pending: "Beklemede",
  confirmed: "Onaylandı",
  rejected: "Reddedildi",
  cancelled: "İptal",
};

const BOTTLE_TYPES = ["Tanqueray", "Casamigos", "Ketel One", "Black Label", "Moët Chandon"] as const;
const AREAS = ["Backstage", "Platform"] as const;
const NONE = "__none__";

function parseTypes(value: string | null | undefined): string[] {
  return (value ?? "")
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
}

function serializeTypes(types: string[], count: number): string | null {
  if (count <= 0) return null;
  const picked = types.slice(0, count).filter((t) => t && t !== NONE);
  return picked.length > 0 ? picked.join(", ") : null;
}

function BottleTypePicker({
  count,
  types,
  onChange,
}: {
  count: number;
  types: string[];
  onChange: (next: string[]) => void;
}) {
  if (count <= 0) return null;
  return (
    <div className="space-y-2">
      <Label>Şişe türleri (opsiyonel)</Label>
      <div className="space-y-2">
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="w-6 shrink-0 text-xs text-muted-foreground">{i + 1}.</span>
            <Select
              value={types[i] ?? NONE}
              onValueChange={(v) => {
                const next = Array.from({ length: count }, (_, j) => types[j] ?? NONE);
                next[i] = v;
                onChange(next);
              }}
            >
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Seçilmedi" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Seçilmedi</SelectItem>
                {BOTTLE_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminReservationsPage() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [filter, setFilter] = useState<Status>("pending");
  const [loadingList, setLoadingList] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [adminNotes, setAdminNotes] = useState<Record<string, string>>({});

  // Manual reservation dialog
  const [manualOpen, setManualOpen] = useState(false);
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [events, setEvents] = useState<Array<{ id: string; name: string; starts_at: string }>>([]);
  const [mEventId, setMEventId] = useState("");
  const [mName, setMName] = useState("");
  const [mContact, setMContact] = useState("");
  const [mPartySize, setMPartySize] = useState("3");
  const [mBottleCount, setMBottleCount] = useState("0");
  const [mBottleTypes, setMBottleTypes] = useState<string[]>([]);
  const [mArea, setMArea] = useState<string>(NONE);
  const [mNotes, setMNotes] = useState("");

  // Edit dialog
  const [editRow, setEditRow] = useState<Row | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [eName, setEName] = useState("");
  const [eContact, setEContact] = useState("");
  const [ePartySize, setEPartySize] = useState("1");
  const [eBottleCount, setEBottleCount] = useState("0");
  const [eBottleTypes, setEBottleTypes] = useState<string[]>([]);
  const [eArea, setEArea] = useState<string>(NONE);
  const [eDate, setEDate] = useState("");
  const [eNotes, setENotes] = useState("");
  const [eStatus, setEStatus] = useState<Status>("pending");

  // PDF export
  const [allEvents, setAllEvents] = useState<Array<{ id: string; name: string; starts_at: string; venue: string | null }>>([]);
  const [pdfEventId, setPdfEventId] = useState("");
  const [pdfBusy, setPdfBusy] = useState<"download" | "share" | null>(null);


  useEffect(() => {
    if (!loading) {
      if (!user) void navigate({ to: "/login" });
      else if (!isAdmin) void navigate({ to: "/dashboard" });
    }
  }, [loading, user, isAdmin, navigate]);

  const load = useCallback(async () => {
    setLoadingList(true);
    const { data, error } = await supabase
      .from("reservations")
      .select("*")
      .eq("status", filter)
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) {
      toast.error(error.message);
      setLoadingList(false);
      return;
    }
    const items = (data ?? []) as Row[];
    const userIds = Array.from(new Set(items.map((r) => r.user_id).filter(Boolean) as string[]));
    const eventIds = Array.from(new Set(items.map((r) => r.event_id)));
    const [{ data: profs }, { data: evs }] = await Promise.all([
      userIds.length
        ? supabase.from("profiles").select("id,first_name,last_name,email,phone").in("id", userIds)
        : Promise.resolve({ data: [] as any[] }),
      eventIds.length
        ? supabase.from("events").select("id,name,starts_at").in("id", eventIds)
        : Promise.resolve({ data: [] as any[] }),
    ]);
    const pMap = new Map((profs ?? []).map((p: any) => [p.id, p]));
    const eMap = new Map((evs ?? []).map((e: any) => [e.id, e]));
    setRows(
      items.map((r) => ({
        ...r,
        profile: r.user_id ? pMap.get(r.user_id) ?? null : null,
        event: eMap.get(r.event_id) ?? null,
      })),
    );
    const notes: Record<string, string> = {};
    items.forEach((r) => (notes[r.id] = r.admin_notes ?? ""));
    setAdminNotes(notes);
    setLoadingList(false);
  }, [filter]);

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin, load]);

  const loadEvents = useCallback(async () => {
    const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    const { data } = await supabase
      .from("events")
      .select("id,name,starts_at")
      .or(`ends_at.gte.${cutoff},and(ends_at.is.null,starts_at.gte.${cutoff})`)
      .order("starts_at", { ascending: true })
      .limit(50);
    setEvents((data ?? []) as any);
  }, []);


  useEffect(() => {
    if (manualOpen) void loadEvents();
  }, [manualOpen, loadEvents]);

  useEffect(() => {
    if (!isAdmin) return;
    void (async () => {
      const { data } = await supabase
        .from("events")
        .select("id,name,starts_at,venue")
        .order("starts_at", { ascending: false })
        .limit(200);
      setAllEvents((data ?? []) as any);
    })();
  }, [isAdmin]);

  const updateStatus = async (id: string, status: Status) => {
    setSavingId(id);
    const { error } = await supabase
      .from("reservations")
      .update({ status, admin_notes: adminNotes[id] || null })
      .eq("id", id);
    setSavingId(null);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Güncellendi");
    void load();
  };

  const toLocalInput = (iso: string) => {
    const d = new Date(iso);
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  };

  const openEdit = (r: Row) => {
    setEditRow(r);
    setEName(r.is_manual ? r.manual_name ?? "" : r.profile ? `${r.profile.first_name} ${r.profile.last_name}` : "");
    setEContact(r.contact ?? r.profile?.phone ?? "");
    setEPartySize(String(r.party_size));
    setEBottleCount(String(r.bottle_count ?? 0));
    setEBottleTypes(parseTypes(r.bottle_type));
    setEArea(r.area ?? NONE);
    setEDate(toLocalInput(r.preferred_date));
    setENotes(r.notes ?? "");
    setEStatus(r.status);
  };

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editRow) return;
    const bottleCount = Math.max(0, Number.parseInt(eBottleCount, 10) || 0);
    setEditSaving(true);
    const payload = {
      party_size: Math.max(1, Number.parseInt(ePartySize, 10) || 1),
      bottle_count: bottleCount,
      bottle_type: serializeTypes(eBottleTypes, bottleCount),
      area: eArea !== NONE ? eArea : null,
      preferred_date: eDate ? new Date(eDate).toISOString() : editRow.preferred_date,
      notes: eNotes.trim() || null,
      contact: eContact.trim() || null,
      status: eStatus,
      admin_notes: adminNotes[editRow.id] || null,
      ...(editRow.is_manual
        ? { manual_name: eName.trim().replace(/\s+/g, " ") || editRow.manual_name }
        : {}),
    };
    const { error } = await supabase.from("reservations").update(payload).eq("id", editRow.id);

    setEditSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Rezervasyon güncellendi");
    setEditRow(null);
    void load();
  };

  const handleExportPdf = async (mode: "download" | "share" = "download") => {
    if (!pdfEventId) {
      toast.error("Etkinlik seçin");
      return;
    }
    setPdfBusy(mode);
    try {
      const ev = allEvents.find((x) => x.id === pdfEventId);
      const { data, error } = await supabase
        .from("reservations")
        .select("*")
        .eq("event_id", pdfEventId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      const items = (data ?? []) as Row[];
      const userIds = Array.from(new Set(items.map((r) => r.user_id).filter(Boolean) as string[]));
      const { data: profs } = userIds.length
        ? await supabase.from("profiles").select("id,first_name,last_name,email,phone").in("id", userIds)
        : { data: [] as any[] };
      const pMap = new Map((profs ?? []).map((p: any) => [p.id, p]));
      const payload = {
        event: {
          name: ev?.name ?? "Etkinlik",
          starts_at: ev?.starts_at ?? new Date().toISOString(),
          venue: ev?.venue ?? null,
        },
        rows: items
          .filter((r) => r.status === "confirmed" || r.status === "pending")
          .map((r) => {
            const p = r.user_id ? pMap.get(r.user_id) : null;
            return {
              name: r.is_manual ? r.manual_name ?? "Manuel" : p ? `${p.first_name} ${p.last_name}` : "Bilinmeyen üye",
              contact: r.contact ?? p?.phone ?? null,
              email: p?.email ?? null,
              party_size: r.party_size,
              bottle_count: r.bottle_count,
              bottle_type: r.bottle_type,
              area: r.area ?? null,
              status: STATUS_LABEL[r.status],
              preferred_date: r.preferred_date,
              notes: r.notes,
              admin_notes: r.admin_notes,
              is_manual: r.is_manual,
            };
          }),
      };
      if (mode === "share") await shareReservationsPdf(payload);
      else await downloadReservationsPdf(payload);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "PDF oluşturulamadı");
    } finally {
      setPdfBusy(null);
    }
  };

  const resetManualForm = () => {
    setMEventId("");
    setMName("");
    setMContact("");
    setMPartySize("3");
    setMBottleCount("0");
    setMBottleTypes([]);
    setMArea(NONE);
    setMNotes("");
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const fullName = mName.trim().replace(/\s+/g, " ");
    if (!mEventId) {
      toast.error("Etkinlik seçin");
      return;
    }
    if (!fullName) {
      toast.error("İsim soyisim zorunlu");
      return;
    }
    const partySize = Math.max(1, Number.parseInt(mPartySize, 10) || 0);
    const bottleCount = Math.max(0, Number.parseInt(mBottleCount, 10) || 0);
    const ev = events.find((x) => x.id === mEventId);
    setManualSubmitting(true);
    const { error } = await supabase.from("reservations").insert({
      event_id: mEventId,
      user_id: null,
      is_manual: true,
      manual_name: fullName,
      contact: mContact.trim() || null,
      party_size: partySize,
      bottle_count: bottleCount,
      bottle_type: serializeTypes(mBottleTypes, bottleCount),
      area: mArea !== NONE ? mArea : null,
      preferred_date: ev ? ev.starts_at : new Date().toISOString(),
      notes: mNotes.trim() || null,
      status: "confirmed",
    });
    setManualSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Manuel rezervasyon eklendi");
    setManualOpen(false);
    resetManualForm();
    setFilter("confirmed");
    void load();
  };


  if (loading || !isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-5xl px-4 py-16 text-center text-muted-foreground">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-5xl px-4 py-10">
        <AdminTabs />
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="mb-2 text-3xl font-bold tracking-tight">Rezervasyonlar</h1>
            <p className="text-sm text-muted-foreground">Üye masa rezervasyon talepleri.</p>
          </div>
          <Dialog
            open={manualOpen}
            onOpenChange={(v) => {
              setManualOpen(v);
              if (!v) resetManualForm();
            }}
          >
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-1.5 h-4 w-4" /> Manuel rezervasyon
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Manuel rezervasyon ekle</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleManualSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Etkinlik *</Label>
                  <Select value={mEventId} onValueChange={setMEventId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Etkinlik seç" />
                    </SelectTrigger>
                    <SelectContent>
                      {events.map((ev) => (
                        <SelectItem key={ev.id} value={ev.id}>
                          {ev.name} — {new Date(ev.starts_at).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="m_name">İsim soyisim *</Label>
                  <Input
                    id="m_name"
                    value={mName}
                    onChange={(e) => setMName(e.target.value)}
                    placeholder="Ad Soyad"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="m_contact">İletişim (telefon / opsiyonel)</Label>
                  <Input
                    id="m_contact"
                    value={mContact}
                    onChange={(e) => setMContact(e.target.value)}
                    placeholder="+90 5xx xxx xx xx"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="m_party">Kişi sayısı</Label>
                    <Input
                      id="m_party"
                      type="number"
                      min={1}
                      max={50}
                      value={mPartySize}
                      onChange={(e) => setMPartySize(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="m_bottles">Şişe adedi (opsiyonel)</Label>
                    <Input
                      id="m_bottles"
                      type="number"
                      min={0}
                      max={20}
                      value={mBottleCount}
                      onChange={(e) => setMBottleCount(e.target.value)}
                    />
                  </div>
                </div>
                <BottleTypePicker
                  count={Math.max(0, Number.parseInt(mBottleCount, 10) || 0)}
                  types={mBottleTypes}
                  onChange={setMBottleTypes}
                />
                <div className="grid grid-cols-2 gap-3">

                  <div className="space-y-2">
                    <Label>Loca yeri (opsiyonel)</Label>
                    <Select value={mArea} onValueChange={setMArea}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seçilmedi" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>Seçilmedi</SelectItem>
                        {AREAS.map((a) => (
                          <SelectItem key={a} value={a}>
                            {a}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="m_notes">Not (opsiyonel)</Label>
                  <Textarea id="m_notes" rows={2} value={mNotes} onChange={(e) => setMNotes(e.target.value)} />
                </div>
                <DialogFooter className="gap-2 sm:gap-2">
                  <Button type="button" variant="ghost" onClick={() => setManualOpen(false)}>
                    Vazgeç
                  </Button>
                  <Button type="submit" disabled={manualSubmitting}>
                    {manualSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ekle"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 shadow-card sm:flex-row sm:items-end">
          <div className="flex-1 space-y-2">
            <Label>Etkinlik bazında liste (PDF)</Label>
            <Select value={pdfEventId} onValueChange={setPdfEventId}>
              <SelectTrigger>
                <SelectValue placeholder="Etkinlik seç" />
              </SelectTrigger>
              <SelectContent>
                {allEvents.map((ev) => (
                  <SelectItem key={ev.id} value={ev.id}>
                    {ev.name} — {new Date(ev.starts_at).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "2-digit" })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => void handleExportPdf("download")} disabled={!!pdfBusy || !pdfEventId}>
              {pdfBusy === "download" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileDown className="mr-1.5 h-4 w-4" />}
              PDF indir
            </Button>
            <Button variant="outline" onClick={() => void handleExportPdf("share")} disabled={!!pdfBusy || !pdfEventId}>
              {pdfBusy === "share" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Share2 className="mr-1.5 h-4 w-4" />}
              İlet
            </Button>
          </div>
        </div>

        <div className="mb-6 flex flex-wrap gap-2">
          {(Object.keys(STATUS_LABEL) as Status[]).map((s) => (
            <Button
              key={s}
              variant={filter === s ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(s)}
            >
              {STATUS_LABEL[s]}
            </Button>
          ))}
        </div>


        {loadingList ? (
          <div className="py-16 text-center text-muted-foreground">
            <Loader2 className="mx-auto h-6 w-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">
            Bu durumda talep yok.
          </div>
        ) : (
          <div className="space-y-4">
            {rows.map((r) => {
              const wa = COMPANY.whatsapp.replace(/\D/g, "");
              const contactRaw = r.contact ?? r.profile?.phone ?? null;
              const phone = contactRaw?.replace(/\D/g, "") ?? "";
              const waTarget = phone || wa;

              const displayName = r.is_manual
                ? r.manual_name ?? "Manuel"
                : r.profile
                  ? `${r.profile.first_name} ${r.profile.last_name}`
                  : "Bilinmeyen üye";
              const waText = encodeURIComponent(
                `Merhaba ${r.profile?.first_name ?? ""}, ${r.event?.name ?? ""} için masa rezervasyon talebiniz hakkında...`,
              );
              return (
                <div
                  key={r.id}
                  className="space-y-3 rounded-2xl border border-border bg-card p-5 shadow-card"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 text-sm font-semibold">
                        <User className="h-4 w-4 text-primary" />
                        {displayName}
                        {r.is_manual && (
                          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                            Manuel
                          </span>
                        )}
                        {contactRaw && (
                          <span className="inline-flex items-center gap-1 font-normal text-muted-foreground">
                            <Phone className="h-3.5 w-3.5" /> {contactRaw}
                          </span>
                        )}

                      </div>
                      {r.profile?.email && (
                        <div className="mt-1 text-xs text-muted-foreground">{r.profile.email}</div>
                      )}
                    </div>
                    <div className="text-right text-xs text-muted-foreground">
                      Talep: {new Date(r.created_at).toLocaleString("tr-TR")}
                    </div>
                  </div>

                  <div className="grid gap-2 text-sm sm:grid-cols-3">
                    <div className="inline-flex items-center gap-1.5">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{r.event?.name ?? "—"}</span>
                    </div>
                    <div className="inline-flex items-center gap-1.5">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      {new Date(r.preferred_date).toLocaleString("tr-TR", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </div>
                    <div className="inline-flex items-center gap-1.5">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      {r.party_size} kişi
                    </div>
                    {(r.bottle_count ?? 0) > 0 && (
                      <div className="inline-flex items-center gap-1.5">
                        <Wine className="h-4 w-4 text-muted-foreground" />
                        {r.bottle_count} şişe{r.bottle_type ? ` (${r.bottle_type})` : ""}
                      </div>
                    )}
                    {r.area && (
                      <div className="inline-flex items-center gap-1.5">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        {r.area}
                      </div>
                    )}
                  </div>

                  {r.notes && (
                    <div className="rounded-lg bg-muted p-3 text-sm">
                      <span className="font-medium">Not:</span> {r.notes}
                    </div>
                  )}

                  <Textarea
                    rows={2}
                    placeholder="Yönetici notu (opsiyonel)"
                    value={adminNotes[r.id] ?? ""}
                    onChange={(e) =>
                      setAdminNotes((s) => ({ ...s, [r.id]: e.target.value }))
                    }
                  />

                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEdit(r)}>
                      <Pencil className="mr-1.5 h-4 w-4" /> Düzenle
                    </Button>
                    {contactRaw && (
                      <Button asChild variant="outline" size="sm">
                        <a
                          href={`https://wa.me/${waTarget}?text=${waText}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <MessageCircle className="mr-1.5 h-4 w-4" /> WhatsApp
                        </a>
                      </Button>
                    )}

                    {r.status !== "confirmed" && (
                      <Button
                        size="sm"
                        disabled={savingId === r.id}
                        onClick={() => void updateStatus(r.id, "confirmed")}
                      >
                        <Check className="mr-1.5 h-4 w-4" /> Onayla
                      </Button>
                    )}
                    {r.status !== "rejected" && (
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={savingId === r.id}
                        onClick={() => void updateStatus(r.id, "rejected")}
                      >
                        <X className="mr-1.5 h-4 w-4" /> Reddet
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <Dialog open={!!editRow} onOpenChange={(v) => !v && setEditRow(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Rezervasyonu düzenle</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditSave} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="e_name">İsim soyisim</Label>
                <Input
                  id="e_name"
                  value={eName}
                  onChange={(ev) => setEName(ev.target.value)}
                  disabled={!editRow?.is_manual}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="e_contact">İletişim</Label>
                <Input
                  id="e_contact"
                  value={eContact}
                  onChange={(ev) => setEContact(ev.target.value)}
                  placeholder="+90 5xx xxx xx xx"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="e_party">Kişi sayısı</Label>
                  <Input
                    id="e_party"
                    type="number"
                    min={1}
                    value={ePartySize}
                    onChange={(ev) => setEPartySize(ev.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="e_bottle">Şişe sayısı</Label>
                  <Input
                    id="e_bottle"
                    type="number"
                    min={0}
                    value={eBottleCount}
                    onChange={(ev) => setEBottleCount(ev.target.value)}
                  />
                </div>
              </div>
              <BottleTypePicker
                count={Math.max(0, Number.parseInt(eBottleCount, 10) || 0)}
                types={eBottleTypes}
                onChange={setEBottleTypes}
              />
              <div className="grid grid-cols-2 gap-3">

                <div className="space-y-2">
                  <Label>Loca yeri</Label>
                  <Select value={eArea} onValueChange={setEArea}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seç" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Belirtilmedi</SelectItem>
                      {AREAS.map((a) => (
                        <SelectItem key={a} value={a}>
                          {a}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="e_date">Tarih ve saat</Label>
                <Input
                  id="e_date"
                  type="datetime-local"
                  value={eDate}
                  onChange={(ev) => setEDate(ev.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Durum</Label>
                <Select value={eStatus} onValueChange={(v) => setEStatus(v as Status)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(STATUS_LABEL) as Status[]).map((s) => (
                      <SelectItem key={s} value={s}>
                        {STATUS_LABEL[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="e_notes">Not</Label>
                <Textarea
                  id="e_notes"
                  rows={3}
                  value={eNotes}
                  onChange={(ev) => setENotes(ev.target.value)}
                />
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                <Button type="button" variant="ghost" onClick={() => setEditRow(null)}>
                  Vazgeç
                </Button>
                <Button type="submit" disabled={editSaving}>
                  {editSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

    </div>
  );
}
